<template>
  <div>
    <div class="table-wrapper">
      <b-tabs v-model="activeTab">
        <b-tab
          title="Active"
          id="active"
          border-variant="light"
          bg-variant="light"
          body-class="px-0 py-0"
        />
        <b-tab
          title="Archived"
          id="archived"
          border-variant="light"
          bg-variant="light"
          body-class="px-0 py-0"
        />
        <b-tab
          title="Deactivated"
          id="deactivated"
          border-variant="light"
          bg-variant="light"
          body-class="px-0 py-0"
        />
      </b-tabs>
      <b-table
        :fields="computedFields"
        :items="items"
        :per-page="perPage"
        :current-page="currentPage"
        :tbody-tr-class="getRowClass"
        class="mt-1 admins-table"
        @row-clicked="viewBusinessAdmin"
        hover
      >
        <template #cell(full_name)="{ item }">
          <div class="cell-content-wrapper">
            <span>
              {{ item.full_name || '-' }}
            </span>
            <div
              v-if="activeTab !== 2 && !item.has_token && !item.is_primary"
              class="lock-overlay"
              :ref="`lock-overlay-${item.id}`"
            >
              <b-icon-lock-fill class="mr-2" style="font-size: 1.1rem" />
              <span>Locked - Assign token to view</span>
            </div>
          </div>
        </template>

        <template #cell(role)="{ item }">
          <b-badge
            v-if="item.has_token || item.is_primary"
            :variant="item.is_project_scoped ? 'info' : 'secondary'"
          >
            {{ item.role_label }}
          </b-badge>
        </template>

        <template #cell(remove)="{ item }">
          <b-button
            v-if="!item.is_primary"
            variant="outline-light"
            style="height: 32px"
            class="px-2"
            title="Removing Business Admin"
            @click="openRemoveModal(item)"
          >
            <b-icon-x-circle-fill variant="danger" />
          </b-button>
        </template>

        <template #cell(purge)="{ item }">
          <div class="text-center">
            <b-button
              variant="outline-danger"
              size="sm"
              style="height: 32px"
              class="px-2"
              title="Permanently delete now"
              @click="openPurgeModal(item)"
            >
              <b-icon-trash-fill />
            </b-button>
          </div>
        </template>

        <template #head(archive)>
          <span class="text-center d-block">{{
            activeTab === 1 ? 'Unarchive' : 'Archive'
          }}</span>
        </template>

        <template #cell(archive)="{ item }">
          <div class="text-center">
            <b-button
              v-if="!item.is_primary"
              size="sm"
              style="
                height: 34px;
                padding: 0.2rem 0.6rem;
                align-items: center;
                display: inline-flex;
                justify-content: center;
              "
              :variant="activeTab === 1 ? 'success' : 'danger'"
              @click="archiveBA(item)"
            >
              <b-icon :icon="activeTab === 1 ? 'archive-fill' : 'archive'" />
            </b-button>
          </div>
        </template>

        <!-- Hide phone/token details when BA is locked (no token & not primary) -->
        <template #cell(phone_number)="{ item }">
          <span v-if="item.has_token || item.is_primary">
            {{ item.phone_number || '-' }}
          </span>
        </template>

        <template #cell(days_remaining)="{ item }">
          <b-badge
            :variant="
              item.days_remaining <= 7
                ? 'danger'
                : item.days_remaining <= 30
                ? 'warning'
                : 'success'
            "
          >
            {{ item.days_remaining }} days
          </b-badge>
        </template>

        <template #cell(token_expiry)="{ item }">
          <span v-if="item.has_token || item.is_primary">
            <span
              :class="{
                'text-danger font-weight-bold': isExpiringSoon(
                  item.token_expiry,
                  30,
                ),
                'text-warning':
                  isExpiringSoon(item.token_expiry, 60) &&
                  !isExpiringSoon(item.token_expiry, 30),
              }"
            >
              {{ formatDate(item.token_expiry) }}
            </span>
          </span>
        </template>
      </b-table>
    </div>
    <b-modal
      v-model="showRemoveModal"
      centered
      size="md"
      @hidden="baToRemove = null"
    >
      <template #modal-title>
        <h3 class="font-weight-bold">Remove Business Admin</h3>
      </template>
      <b-row class="mb-3">
        <b-col>
          Remove <b>{{ baToRemove ? baToRemove.full_name : '' }}</b> from this
          company?
        </b-col>
      </b-row>

      <!-- Primary BAs may escalate to an immediate, permanent delete. -->
      <b-form-group v-if="currentUserIsPrimaryBa" style="margin-bottom: 1.5rem">
        <b-form-radio
          v-model="removeMode"
          name="remove-mode"
          value="retention"
          class="mb-3"
        >
          <span class="font-weight-bold d-block">30-day retention</span>
          <div class="grey--text small mt-1">
            Access is revoked immediately, but the account is retained for 30
            days and may be restored within that period. It is permanently
            deleted thereafter.
          </div>
        </b-form-radio>
        <b-form-radio v-model="removeMode" name="remove-mode" value="instant">
          <span class="font-weight-bold text-danger d-block"
            >Permanently delete now</span
          >
          <div class="grey--text small mt-1">
            The account is deleted immediately. Their files are transferred to
            you; all other data is removed permanently and cannot be restored.
          </div>
        </b-form-radio>
      </b-form-group>

      <template #modal-footer>
        <div class="d-flex" style="gap: 16px">
          <b-button variant="secondary" @click="showRemoveModal = false">
            Cancel
          </b-button>
          <b-button
            :variant="removeMode === 'instant' ? 'danger' : 'primary'"
            :disabled="removing"
            @click="confirmRemove"
          >
            {{ removeMode === 'instant' ? 'Permanently Delete' : 'Remove' }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Instant delete from the deactivated (retention) tab — primary BA only -->
    <b-modal
      v-model="showPurgeModal"
      centered
      size="md"
      @hidden="baToPurge = null"
    >
      <template #modal-title>
        <h3 class="font-weight-bold">Permanently Delete Account</h3>
      </template>
      <p style="margin-bottom: 1.5rem">
        This permanently deletes
        <b>{{ baToPurge ? baToPurge.full_name : '' }}</b> now, skipping the
        remaining retention period. Their files are transferred to you; all
        other data is removed permanently and cannot be restored.
      </p>
      <template #modal-footer>
        <div class="d-flex" style="gap: 16px">
          <b-button variant="secondary" @click="showPurgeModal = false">
            Cancel
          </b-button>
          <b-button variant="danger" :disabled="purging" @click="confirmPurge">
            Permanently Delete
          </b-button>
        </div>
      </template>
    </b-modal>

    <assign-token-modal
      v-if="isAssigningToken && canAssignTokens"
      :showModal="isAssigningToken"
      :tradieToAssign="businessAdminToAssign"
      @closeModal="onAssignModalClose"
      @assign="onAssignTokenToBa"
      @purchaseTokens="onAssignModalPurchaseTokens"
    />

    <purchase-token-modal
      v-if="canPurchaseTokens"
      :showModal="isPurchasingToken"
      @closeModal="purchasingToken(false)"
      @purchase="onPurchaseTokens"
    />

    <b-pagination
      v-if="rows > perPage"
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      aria-controls="my-table"
      :hide-goto-end-buttons="true"
      align="center"
    />
  </div>
</template>

<script>
import { canBuyTokens } from '@/utils/tokenAccess'
import { mapGetters, mapActions } from 'vuex'
import AssignTokenModal from '@/components/modals/AssignTokenModal.vue'
import PurchaseTokenModal from '@/components/modals/PurchaseTokenModal.vue'

export default {
  components: {
    AssignTokenModal,
    PurchaseTokenModal,
  },

  props: {
    search: { type: String, default: '' },
    isPrimaryBusinessAdmin: { type: Boolean, default: false },
  },

  data() {
    return {
      items: [],
      activeTab: 0,

      showRemoveModal: false,
      baToRemove: null,
      removing: false,
      removeMode: 'retention',

      showPurgeModal: false,
      baToPurge: null,
      purging: false,

      perPage: 10,
      currentPage: 1,

      isAssigningToken: false,
      businessAdminToAssign: null,

      isPurchasingToken: false,
    }
  },

  computed: {
    ...mapGetters('company', ['tradies', 'tradiesMeta', 'tradie', 'files']),
    ...mapGetters('auth', ['user', 'hasPermission']),

    rows() {
      return this.items.length
    },

    /** True when the current user can manage (add/remove/archive) business admins. */
    canManageBas() {
      return (
        this.isPrimaryBusinessAdmin ||
        this.hasPermission('settings.users.manage')
      )
    },

    canPurchaseTokens() {
      return canBuyTokens(
        this.user,
        (key) => this.isPrimaryBusinessAdmin || this.hasPermission(key),
      )
    },
    canAssignTokens() {
      return (
        this.isPrimaryBusinessAdmin ||
        this.hasPermission('settings.billing.assign')
      )
    },

    computedFields() {
      // Responsive priority columns: essentials show on phones; lower-priority
      // columns appear progressively as the screen widens (no horizontal scroll).
      // `atSm/atMd/...` hide the column below that breakpoint via Bootstrap's
      // responsive table-cell display utilities.
      const atSm = 'd-none d-sm-table-cell'
      const atMd = 'd-none d-md-table-cell'
      const atLg = 'd-none d-lg-table-cell'
      const atXl = 'd-none d-xl-table-cell'

      if (this.activeTab === 2) {
        const deletedFields = [
          { key: 'full_name', label: 'Name' },
          {
            key: 'days_remaining',
            label: 'Days Remaining',
            thClass: atSm,
            tdClass: atSm,
          },
          { key: 'email', label: 'Email', thClass: atMd, tdClass: atMd },
          {
            key: 'deleted_at',
            label: 'Deleted On',
            thClass: atLg,
            tdClass: atLg,
          },
          {
            key: 'expires_at',
            label: 'Expires On',
            thClass: atXl,
            tdClass: atXl,
          },
        ]
        // Only the primary BA may escalate a retention account to an instant delete.
        if (this.currentUserIsPrimaryBa) {
          deletedFields.push({ key: 'purge', label: '' })
        }
        return deletedFields
      }
      const base = [
        { key: 'full_name', label: 'Name' },
        { key: 'role', label: 'Role', thClass: atSm, tdClass: atSm },
        { key: 'email', label: 'Email', thClass: atMd, tdClass: atMd },
        {
          key: 'phone_number',
          label: 'Phone Number',
          thClass: atLg,
          tdClass: `cell-hide-when-locked ${atLg}`,
        },
        {
          key: 'token_expiry',
          label: 'Token Expiry',
          thClass: atXl,
          tdClass: `cell-hide-when-locked ${atXl}`,
        },
      ]
      if (this.canManageBas) {
        if (this.activeTab === 0) base.push('remove')
        base.push('archive')
      }
      return base
    },

    currentUserIsPrimaryBa() {
      return this.isPrimaryBusinessAdmin
    },
  },

  watch: {
    search: {
      immediate: true,
      handler() {
        this.loadBusinessAdmins()
      },
    },
    activeTab() {
      this.currentPage = 1
      this.loadBusinessAdmins()
    },
  },

  mounted() {
    this.loadBusinessAdmins()
  },

  methods: {
    ...mapActions('token', {
      purchase: 'purchase',
    }),

    purchasingToken(status) {
      this.isPurchasingToken = status
    },

    async onPurchaseTokens(quantity) {
      if (!quantity || quantity < 1) return
      await this.purchase(quantity).catch((err) => console.error(err))
      this.purchasingToken(false)
    },

    onAssignModalPurchaseTokens() {
      this.isAssigningToken = false
      this.businessAdminToAssign = null
      if (this.canPurchaseTokens) {
        this.purchasingToken(true)
      }
    },

    getRowClass(item) {
      if (!item || this.activeTab === 2) return ''
      if (!item.has_token && !item.is_primary) return 'row-locked'
      return ''
    },

    async loadBusinessAdmins() {
      this.items = []
      if (this.activeTab === 2) {
        const { data } = await this.$api.get('company/business-admins', {
          params: { search: this.search, deleted_only: true },
        })
        this.items = (data.data || data).map((item) => ({
          ...item,
          deleted_at: this.formatDate(item.deleted_at),
          expires_at: this.formatDate(item.expires_at),
        }))
        return
      }
      const { data } = await this.$api.get('company/business-admins', {
        params: { search: this.search, archived_only: this.activeTab === 1 },
      })
      const raw = data.data || data
      this.items = raw.map((item) => this.normaliseItem(item))
      const additionalBasCount = this.items.filter(
        (item) => !item.is_primary,
      ).length
      this.$emit('ba-count-changed', additionalBasCount)
    },

    async archiveBA(item) {
      try {
        const isArchiving = this.activeTab === 0
        await this.$api.post(`company/business-admins/${item.id}/archive`, {
          is_archived: isArchiving,
        })
        await this.loadBusinessAdmins()
      } catch (error) {
        console.error(error)
      }
    },

    openRemoveModal(item) {
      this.baToRemove = item
      this.removeMode = 'retention'
      this.showRemoveModal = true
    },

    async confirmRemove() {
      if (!this.baToRemove) return
      this.removing = true
      try {
        await this.$api.delete(
          `company/business-admins/${this.baToRemove.id}`,
          { data: { mode: this.removeMode } },
        )
        this.showRemoveModal = false
        this.baToRemove = null
        await this.loadBusinessAdmins()
      } catch (e) {
        console.error(e)
      } finally {
        this.removing = false
      }
    },

    openPurgeModal(item) {
      this.baToPurge = item
      this.showPurgeModal = true
    },

    async confirmPurge() {
      if (!this.baToPurge) return
      this.purging = true
      try {
        await this.$api.delete(
          `company/business-admins/${this.baToPurge.id}/purge`,
        )
        this.showPurgeModal = false
        this.baToPurge = null
        await this.loadBusinessAdmins()
      } catch (e) {
        console.error(e)
      } finally {
        this.purging = false
      }
    },

    async onAssignTokenToBa() {
      if (!this.businessAdminToAssign) return

      try {
        await this.$api.post('company/assign-token-to-ba', {
          user_id: this.businessAdminToAssign.id, // BA’s user id
        })

        this.isAssigningToken = false
        this.businessAdminToAssign = null

        await this.loadBusinessAdmins()
        await this.$store.dispatch('token/getBalance')
      } catch (error) {
        // show an error toast / console.log
        console.error(error)
      }
    },

    onAssignModalClose() {
      this.isAssigningToken = false
      this.businessAdminToAssign = null
    },

    assigningToken(item) {
      this.businessAdminToAssign = item
      this.isAssigningToken = true
    },

    async viewBusinessAdmin(item) {
      if (this.activeTab === 2) return
      if (!item.has_token && !item.is_primary) {
        if (this.canAssignTokens) this.assigningToken(item)
        return
      }

      this.$router.push({
        name: 'business-admins.view-business-admin',
        params: {
          businessAdminId: item.id,
        },
      })
    },

    /** Prefer the backend's role_name; fall back to the legacy admin/supervisor label. */
    roleLabel(raw, user) {
      const name = raw.role_name || (user && user.role_name)
      if (name) return name
      const role = raw.role || (user && user.role)
      if (role === 'supervisor') return 'Supervisor'
      return 'Admin'
    },

    normaliseItem(raw) {
      const user = raw.user || raw
      const token = raw.token || null
      return {
        ...raw,
        id: raw.id || raw.user_id || (user && user.id),
        full_name: raw.full_name || (user && user.full_name),
        email: raw.email || (user && user.email),
        phone_number: raw.phone_number || (user && user.phone_number),
        is_primary: !!(raw.is_primary != null
          ? raw.is_primary
          : user && user.is_primary),
        has_token: !!(raw.has_token != null
          ? raw.has_token
          : user && user.has_token),
        is_archived: !!(raw.is_archived != null
          ? raw.is_archived
          : user && user.is_archived),
        role:
          raw.role || (user && user.role) || (raw.is_primary ? 'admin' : null),
        role_name: raw.role_name || (user && user.role_name) || null,
        is_project_scoped: !!(raw.is_project_scoped != null
          ? raw.is_project_scoped
          : user && user.is_project_scoped != null
          ? user.is_project_scoped
          : (raw.role || (user && user.role)) === 'supervisor'),
        role_label: this.roleLabel(raw, user),
        token_expiry:
          (token && token.expires_at) ||
          raw.token_expiry ||
          raw.token_expires_at ||
          raw.expires_at ||
          null,
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    isExpiringSoon(dateString, days) {
      if (!dateString) return false
      const diff = new Date(dateString) - new Date()
      return diff > 0 && diff < days * 24 * 60 * 60 * 1000
    },
  },
}
</script>

<style>
.row-locked > td {
  background-color: #f5f5f5 !important;
}

.row-locked > td.cell-hide-when-locked * {
  visibility: hidden;
}

.lock-overlay {
  color: #000000 !important;
}

.admins-table tbody tr {
  cursor: pointer;
}

/* Keep the "Locked" overlay confined to the row width so it wraps inside the
   row instead of spilling past the card. Spanning left:0 → right:0 makes it
   exactly the row's width at every screen size, so it scales with the row. */
.admins-table tr.row-locked .lock-overlay {
  left: 0;
  right: 0;
  width: auto;
  max-width: 100%;
  transform: translateY(-50%);
  white-space: normal;
  text-align: center;
  padding: 0 6px;
}

/* Phones: pull the columns together so the table fits inside the card without
   scrolling. Tighter cell padding + slightly smaller text; action buttons are
   compacted too. Desktop spacing is untouched (only applies < 576px). */
@media (max-width: 575.98px) {
  .admins-table th,
  .admins-table td {
    padding-left: 0.3rem;
    padding-right: 0.3rem;
    font-size: 0.85rem;
    vertical-align: middle;
    /* Long emails/names are unbreakable strings; allow them to wrap so a single
       cell can't push the table past the edge of the card. */
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  .admins-table td .btn {
    padding-left: 0.3rem !important;
    padding-right: 0.3rem !important;
  }
  /* Phones can't fit "Locked - Assign token to view", so show just the lock
     icon (the muted row content still signals it's locked). */
  .admins-table tr.row-locked .lock-overlay span {
    display: none;
  }
  .admins-table tr.row-locked .lock-overlay .b-icon {
    margin-right: 0 !important;
    font-size: 1.35rem !important;
  }
}
</style>
